import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  id: "organization_profile_general",
  class: "w-full"
}
const _hoisted_2 = { class: "field" }
const _hoisted_3 = { class: "field" }
const _hoisted_4 = { class: "field" }
const _hoisted_5 = { class: "field" }
const _hoisted_6 = { class: "formgrid grid field col mb-0" }
const _hoisted_7 = {
  key: 0,
  class: "field"
}

import { computed, onMounted, ref } from 'vue';
import TreeSelect from 'primevue/treeselect';
import useVuelidate from '@vuelidate/core';
import { required, requiredIf } from '@vuelidate/validators';
import { useOrganizationStore, useOrganizationTypeStore } from '@/store';
import { CustomerSuccessPartnerInterface, OrganizationInterface, OrganizationsParentsInterface } from '@/types';
import { can } from '@/composables';
import { PERMISSIONS } from '@/enum/permissions';
import { useCustomerSuccessPartnerStore } from '@/store/CustomerSuccessPartner';

export default /*@__PURE__*/_defineComponent({
  __name: 'General',
  setup(__props) {

const organization = ref({} as OrganizationInterface);

/**
 * All of the state references including the convertion of the
 * organization state object into a ref.
 */
const organizationStore = useOrganizationStore();
const organizationTypeStore = useOrganizationTypeStore();
const customerSuccessPartnerStore = useCustomerSuccessPartnerStore();
const parentError = ref('Cannot Add This Organization As A Parent');
const customerSuccessPartners = ref<CustomerSuccessPartnerInterface[]>([]);
const noSelectionEntry = {
  display_name: 'No selection',
  id: -1,
  first_name: '',
  last_name: '',
  person_id: 0,
  organizations: [],
  active: 0,
};
/**
 * The required validation rules.
 */
const rules = {
  name: { required },
  acronym: {},
  organization_type_id: { required },
  external_id: {},
  parent_id: {
    requiredIfCustom: requiredIf(() => !canCreateRootOrganization.value),
  },
  customer_success_partner_id: {},
};

/**
 * Initialize the vuelidate instance.
 */
const v$ = useVuelidate(rules, organization);
const canEdit = computed(() => can([PERMISSIONS.ORGANIZATION_UPDATE]));
/**
 * Throw all errors here for easier access.
 */
const errors = ref(null);

/**
 * Determines whether or not the form has been submitted.
 */
const submitted = ref(false);

/**
 * Initialize everything required to build out the component.
 */
onMounted(() => init());

/**
 * Assign the static organization identifier.
 */
const id = computed(() => organization.value.id);

const canCreateRootOrganization = computed<boolean>(() => {
  return can([PERMISSIONS.ORGANIZATION_CREATE_ROOT]);
});

/**
 * Define the request update payload.
 */
const payload = computed(() => {
  const value = organization.value;

  const { parent_id } = value;
  value.parent_id = Object.keys(parent_id)[0] ? Number(Object.keys(parent_id)[0]) : parent_id;

  return {
    name: value.name,
    acronym: value.acronym,
    organization_type_id: value.organization_type_id,
    external_id: value.external_id,
    parent_id: value.parent_id,
    customer_success_partner_id: value.customer_success_partner_id,
  };
});

/**
 * Initialize the organization and the available types.
 */
const init = async () => {
  await organizationTypeStore.getOrganizationTypes();
  await initCustomerSuccessPartners();
  setOrganizationAndParentList();
};

const initCustomerSuccessPartners = async () => {
  await customerSuccessPartnerStore.getCustomerSuccessPartners();
  customerSuccessPartners.value = [noSelectionEntry, ...customerSuccessPartnerStore.customerSuccessPartners];
};

/**
 * call parent orgs and set the org.  this is used again when
 * we update so we seperate from init since we dont need org types again
 */
const setOrganizationAndParentList = async () => {
  await organizationStore.getParentsOrganizations();
  await setOrganization();
};

/**
 * Return a cloned version of the state organization object.
 */
const clonedOrganization = () => {
  return { ...organizationStore.organization };
};

/**
 * After waiting for data to use from the API define the organization
 * ref based on a clone of the current object within state.
 */
const setOrganization = async () => (organization.value = clonedOrganization());

/**
 * Reset the vuelidate dirty status for all keys.
 */
const resetDirty = () => v$.value.$reset();

/**
 * Get the field styles based on the specific key.
 */
const fieldStyles = (key: string, styles?: string) => {
  const validation =
    (v$.value[key].$invalid && v$.value[key].$dirty) || (errors.value && errors.value[key]) ? 'p-invalid' : '';
  return `${styles} ${validation}`;
};

/**
 * Handle the cancellation of all changes made to the organization
 * by restting the values and statuses.
 */
const onCancel = () => {
  organization.value = clonedOrganization();
  errors.value = null;
  resetDirty();
};

/**
 * Handle the client side validation, followed by saving of all changes made
 * to the organization.
 */
const onSave = async () => {
  submitted.value = true;

  // Don't allow a submit event unless the fields are valid.
  if (v$.value.$invalid) return;

  // if no selection is selected for CSP, send null in the org update request
  if (!payload.value.customer_success_partner_id || payload.value.customer_success_partner_id < 0) {
    payload.value.customer_success_partner_id = null;
  }

  await organizationStore.updateOrganization(payload.value, id.value as number);
  onCancel();
  await setOrganizationAndParentList();
};

/**
 * boolean check if parent can be added to org
 * @param parentId
 */
const cannotAddOrg = (parentId: number): boolean => {
  const origParentId = organizationStore.organization.parent_id;
  const origId = organizationStore.organization.id;

  //if trying to add to itself or already a child of that org
  if (origParentId === parentId) {
    parentError.value = 'This Organization is already a child of this Parent';
    return true;
  }
  if (origId === parentId) {
    parentError.value = "You cannot select this Organization as it's own Parent";
    return true;
  }

  let cantAdd = false;
  if (organizationStore.organization.children.length) {
    cantAdd = lookForChildKey(organizationStore.organization.children as OrganizationsParentsInterface[], parentId);
  }
  return cantAdd;
};

/**
 * recursive function to seek infinite childrens key
 * @param arr
 */
const lookForChildKey = (arr: OrganizationsParentsInterface[], parentId: number) => {
  for (let i = 0; i < arr.length; i++) {
    if (arr[i].data.parent_id == parentId || arr[i].key == parentId) {
      return true;
    } else if (arr[i].children.length) {
      lookForChildKey(arr[i].children as OrganizationsParentsInterface[], parentId);
    }
  }
  return false;
};

/**
 * you cannot select the org, or the orgs children as a parent so find it here and
 * notify if not allowed and reset the org id
 */
const checkOrganizationForChildren = (e: string[]) => {
  const key = Object.keys(e)[0];

  if (cannotAddOrg(parseInt(key))) {
    organization.value.parent_id = organizationStore.organization.parent_id;
    organizationStore.doToast(parentError.value, 'Error', true);
  }
};

return (_ctx: any,_cache: any) => {
  const _component_Skeleton = _resolveComponent("Skeleton")!
  const _component_InputText = _resolveComponent("InputText")!
  const _component_Dropdown = _resolveComponent("Dropdown")!
  const _component_FormCard = _resolveComponent("FormCard")!

  return (_openBlock(), _createBlock(_component_FormCard, {
    title: "General",
    dirty: _unref(v$).$anyDirty && canEdit.value,
    cancel: onCancel,
    save: onSave,
    "save-disabled": _unref(v$).$anyDirty && _unref(v$).$invalid
  }, {
    form: _withCtx(() => [
      _createElementVNode("form", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _cache[6] || (_cache[6] = _createElementVNode("label", {
            class: "required",
            for: "organization_profile_general_name"
          }, "Name", -1)),
          (_unref(organizationStore).loading)
            ? (_openBlock(), _createBlock(_component_Skeleton, {
                key: 0,
                height: "2.3rem"
              }))
            : (_openBlock(), _createBlock(_component_InputText, {
                key: 1,
                id: "organization_profile_general_name",
                modelValue: _unref(v$).name.$model,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(v$).name.$model) = $event)),
                type: "text",
                class: _normalizeClass(fieldStyles('name')),
                placeholder: "Organization Name",
                disabled: !canEdit.value
              }, null, 8, ["modelValue", "class", "disabled"]))
        ]),
        _createElementVNode("div", _hoisted_3, [
          _cache[7] || (_cache[7] = _createElementVNode("label", {
            class: "required",
            for: "organization_profile_general_acronym"
          }, "Acronym", -1)),
          (_unref(organizationStore).loading)
            ? (_openBlock(), _createBlock(_component_Skeleton, {
                key: 0,
                height: "2.3rem"
              }))
            : (_openBlock(), _createBlock(_component_InputText, {
                key: 1,
                id: "organization_profile_general_acronym",
                modelValue: _unref(v$).acronym.$model,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(v$).acronym.$model) = $event)),
                type: "text",
                class: _normalizeClass(fieldStyles('acronym')),
                placeholder: "Organization Acronym",
                disabled: !canEdit.value
              }, null, 8, ["modelValue", "class", "disabled"]))
        ]),
        _createElementVNode("div", _hoisted_4, [
          _cache[8] || (_cache[8] = _createElementVNode("label", {
            class: "required",
            for: "organization_profile_general_type_id"
          }, "Type", -1)),
          (_unref(organizationStore).loading)
            ? (_openBlock(), _createBlock(_component_Skeleton, {
                key: 0,
                height: "2.3rem"
              }))
            : (_openBlock(), _createBlock(_component_Dropdown, {
                key: 1,
                id: "organization_profile_general_type_id",
                modelValue: _unref(v$).organization_type_id.$model,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(v$).organization_type_id.$model) = $event)),
                options: _unref(organizationTypeStore).types,
                "option-label": "title",
                "option-value": "id",
                loading: _unref(organizationTypeStore).loading,
                placeholder: "Select a Type",
                class: _normalizeClass(fieldStyles('organization_type_id')),
                disabled: !canEdit.value
              }, null, 8, ["modelValue", "options", "loading", "class", "disabled"]))
        ]),
        _createElementVNode("div", _hoisted_5, [
          _cache[9] || (_cache[9] = _createElementVNode("label", { for: "organization_profile_general_external_id" }, "SSID", -1)),
          (_unref(organizationStore).loading)
            ? (_openBlock(), _createBlock(_component_Skeleton, {
                key: 0,
                height: "2.3rem"
              }))
            : (_openBlock(), _createBlock(_component_InputText, {
                key: 1,
                id: "organization_profile_general_external_id",
                modelValue: _unref(v$).external_id.$model,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_unref(v$).external_id.$model) = $event)),
                type: "text",
                class: _normalizeClass(fieldStyles('external_id')),
                placeholder: "SSID",
                disabled: !canEdit.value
              }, null, 8, ["modelValue", "class", "disabled"]))
        ]),
        _createElementVNode("div", _hoisted_6, [
          _cache[10] || (_cache[10] = _createElementVNode("label", {
            class: "required",
            for: "type"
          }, "Parent Organization", -1)),
          (_unref(organizationStore).loading)
            ? (_openBlock(), _createBlock(_component_Skeleton, {
                key: 0,
                height: "2.3rem"
              }))
            : (_openBlock(), _createBlock(_unref(TreeSelect), {
                key: 1,
                id: "parent_organization",
                modelValue: _unref(v$).parent_id.$model,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((_unref(v$).parent_id.$model) = $event)),
                options: _unref(organizationStore).organizationParents,
                "option-label": "label",
                "option-value": "key",
                loading: _unref(organizationStore).loading,
                placeholder: "Select Parent Organization",
                disabled: !canEdit.value,
                style: {"font-weight":"bold"},
                class: "w-full",
                onChange: checkOrganizationForChildren
              }, null, 8, ["modelValue", "options", "loading", "disabled"]))
        ]),
        (_unref(customerSuccessPartnerStore).canGetCustomerSuccessPartners)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
              _cache[11] || (_cache[11] = _createElementVNode("label", { for: "organization_profile_general_customer_success_partner_id" }, "CSP", -1)),
              (_unref(organizationStore).loading)
                ? (_openBlock(), _createBlock(_component_Skeleton, {
                    key: 0,
                    height: "2.3rem"
                  }))
                : (_openBlock(), _createBlock(_component_Dropdown, {
                    key: 1,
                    id: "organization_profile_general_customer_success_partner_id",
                    modelValue: _unref(v$).customer_success_partner_id.$model,
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((_unref(v$).customer_success_partner_id.$model) = $event)),
                    options: customerSuccessPartners.value,
                    "option-label": "display_name",
                    "option-value": "id",
                    loading: _unref(customerSuccessPartnerStore).loading,
                    placeholder: "Select a CSP",
                    disabled: !canEdit.value
                  }, null, 8, ["modelValue", "options", "loading", "disabled"]))
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }, 8, ["dirty", "save-disabled"]))
}
}

})